import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Search, Eye, PauseCircle, KeyRound, XCircle, Home } from 'lucide-react';
import api from '../../../api';
import { formatMelbourneShortDate } from '../../../utils/melbourneTime';

/**
 * Buyer search status on a contact record (Monday 13068998532, CG 17 Sep 2026).
 *
 * "Are you still actually looking to buy?" — the buyer answers it themselves
 * from the portal or from the link in any of our emails, and an agent sets it
 * here after a phone call. Same endpoint, same service, so the two can never
 * disagree.
 *
 * This is NOT the marketing subscription above it. Marking someone "purchased"
 * stops property alerts; it does not unsubscribe them, and unsubscribing does
 * not change their search status.
 *
 * Michael Piccoli, 29 Sep 2026: "It would be good to view where they have
 * purchased in this list and the date of purchase if this was manually entered
 * in by the agents." Where they bought was only ever asked of the BUYER, on the
 * portal and on the emailed page, so an agent taking the call had nowhere to
 * put it and the purchased lists came back blank. Both fields are now typed
 * here, on either purchased branch, and both show in the analytics drill-in.
 */

// Mirrors core/utils/buyer_lifecycle_choices.py.
const CHOICES = [
    { id: 'active', label: 'Actively looking', icon: Search },
    { id: 'watching', label: 'Keeping an eye on the market', icon: Eye },
    { id: 'paused', label: 'Paused', icon: PauseCircle },
    { id: 'purchased_collings', label: 'Purchased through Collings', icon: KeyRound },
    { id: 'purchased_elsewhere', label: 'Purchased elsewhere', icon: KeyRound },
    { id: 'not_looking', label: 'No longer looking', icon: XCircle },
    { id: 'rented', label: 'Recently rented', icon: Home },
];

// Both purchased branches ask the same two questions.
const PURCHASED = ['purchased_collings', 'purchased_elsewhere'];

const REASONS = [
    { id: 'purchased_elsewhere', label: 'Purchased elsewhere' },
    { id: 'plans_changed', label: 'Plans changed' },
    { id: 'finance', label: 'Finance / budget' },
    { id: 'waiting_market', label: 'Waiting for the market' },
    { id: 'not_right_now', label: 'Not looking right now' },
    { id: 'other', label: 'Other' },
];

const ContactSearchStatusPanel = ({ contactId, canEdit = true }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [snapshot, setSnapshot] = useState(null);
    const [status, setStatus] = useState('');
    const [pausedUntil, setPausedUntil] = useState('');
    const [reason, setReason] = useState('');
    const [note, setNote] = useState('');
    const [purchaseSuburb, setPurchaseSuburb] = useState('');
    const [purchaseDate, setPurchaseDate] = useState('');

    const apply = useCallback((data) => {
        setSnapshot(data || null);
        setStatus(data?.status || '');
        setPausedUntil(data?.paused_until || '');
        setReason(data?.reason || '');
        setNote(data?.note || '');
        setPurchaseSuburb(data?.purchase_suburb || '');
        setPurchaseDate(data?.purchase_date || '');
    }, []);

    useEffect(() => {
        if (!contactId) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`api/buyer-portal/contacts/${contactId}/search-status/`);
                if (!cancelled) apply(data?.status);
            } catch (error) {
                // The rest of the contact record is unaffected.
                console.error('Failed to load search status:', error);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [contactId, apply]);

    const dirty = useMemo(() => (
        status !== (snapshot?.status || '')
        || (['paused', 'rented'].includes(status) && pausedUntil !== (snapshot?.paused_until || ''))
        || (status === 'not_looking' && (
            reason !== (snapshot?.reason || '')
            || (reason === 'other' && note !== (snapshot?.note || ''))
        ))
        || (PURCHASED.includes(status) && (
            purchaseSuburb !== (snapshot?.purchase_suburb || '')
            || purchaseDate !== (snapshot?.purchase_date || '')
        ))
    ), [status, pausedUntil, reason, note, purchaseSuburb, purchaseDate, snapshot]);

    const handleSave = async () => {
        if (!status) return;
        try {
            setSaving(true);
            const payload = { status };
            if (status === 'paused') {
                // An agent always sets an explicit date, or leaves it open.
                payload.paused_until = pausedUntil || null;
                payload.pause_option = pausedUntil ? 'date' : 'indefinite';
            }
            if (status === 'rented') {
                // Renting for now (CRM-2026-00132). Blank = the backend's
                // default return, 12 months.
                payload.paused_until = pausedUntil || null;
            }
            if (status === 'not_looking') {
                payload.reason = reason;
                payload.note = reason === 'other' ? note : '';
            }
            if (PURCHASED.includes(status)) {
                payload.purchase_suburb = purchaseSuburb.trim();
                // Null rather than '' so clearing the box clears the date.
                payload.purchase_date = purchaseDate || null;
            }
            const { data } = await api.post(
                `api/buyer-portal/contacts/${contactId}/search-status/`, payload);
            apply(data?.status);
            toast.success('Search status updated.');
        } catch (error) {
            console.error('Failed to save search status:', error);
            toast.error('Could not update the search status.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return null;

    return (
        <div className="basis-full mt-2 pt-4 border-t border-gray-100">
            <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
                <label className="text-sm font-medium text-gray-700">Buyer search status</label>
                {snapshot?.updated_at && (
                    <span className="text-xs text-gray-400">
                        {snapshot.source === 'buyer' ? 'Set by the buyer' : 'Set by an agent'}
                        {` · ${formatMelbourneShortDate(snapshot.updated_at)}`}
                    </span>
                )}
            </div>
            <p className="text-xs text-gray-400 mb-3">
                Are they still actually looking? Separate from the marketing toggles above,
                this only controls property alerts.
            </p>

            <div className="flex flex-wrap gap-2">
                {CHOICES.map(({ id, label, icon: Icon }) => (
                    <button
                        key={id}
                        type="button"
                        disabled={!canEdit}
                        onClick={() => {
                            const next = status === id ? '' : id;
                            setStatus(next);
                            // Paused and Recently rented each have their own
                            // date; never carry one over to the other.
                            setPausedUntil(next === snapshot?.status ? (snapshot?.paused_until || '') : '');
                        }}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors disabled:opacity-50 ${
                            status === id
                                ? 'bg-[#55C5D0]/15 border-[#55C5D0] text-[#0e7490]'
                                : 'bg-white border-gray-200 text-gray-600 hover:border-[#55C5D0]/50'
                        }`}
                    >
                        <Icon className="w-3.5 h-3.5" />
                        {label}
                    </button>
                ))}
            </div>

            {status === 'paused' && (
                <div className="mt-3 flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-600">
                        Paused until (leave blank for no end date)
                    </label>
                    <input
                        type="date"
                        value={pausedUntil || ''}
                        disabled={!canEdit}
                        onChange={(e) => setPausedUntil(e.target.value)}
                        className="w-full sm:w-48 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/40"
                    />
                </div>
            )}

            {status === 'rented' && (
                <div className="mt-3 flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-600">
                        Back to buying on (leave blank for 12 months). You get a reminder to call them that day.
                    </label>
                    <input
                        type="date"
                        aria-label="Back to buying on"
                        value={pausedUntil || ''}
                        disabled={!canEdit}
                        onChange={(e) => setPausedUntil(e.target.value)}
                        className="w-full sm:w-48 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/40"
                    />
                </div>
            )}

            {status === 'not_looking' && (
                <div className="mt-3">
                    <label className="text-xs font-medium text-gray-600 block mb-1.5">Reason</label>
                    <div className="flex flex-wrap gap-2">
                        {REASONS.map(({ id, label }) => (
                            <button
                                key={id}
                                type="button"
                                disabled={!canEdit}
                                onClick={() => setReason(reason === id ? '' : id)}
                                className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors disabled:opacity-50 ${
                                    reason === id
                                        ? 'bg-[#55C5D0]/15 border-[#55C5D0] text-[#0e7490]'
                                        : 'bg-white border-gray-200 text-gray-600 hover:border-[#55C5D0]/50'
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                    {reason === 'other' && (
                        <input
                            type="text"
                            value={note}
                            maxLength={255}
                            disabled={!canEdit}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Add the reason they gave"
                            className="mt-2 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/40"
                        />
                    )}
                </div>
            )}

            {PURCHASED.includes(status) && (
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-gray-600" htmlFor="buyer-purchase-suburb">
                            Where did they buy?
                        </label>
                        <input
                            id="buyer-purchase-suburb"
                            type="text"
                            value={purchaseSuburb}
                            maxLength={255}
                            disabled={!canEdit}
                            onChange={(e) => setPurchaseSuburb(e.target.value)}
                            placeholder="Suburb or address"
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/40"
                        />
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-gray-600" htmlFor="buyer-purchase-date">
                            Date of purchase
                        </label>
                        <input
                            id="buyer-purchase-date"
                            type="date"
                            value={purchaseDate || ''}
                            disabled={!canEdit}
                            onChange={(e) => setPurchaseDate(e.target.value)}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/40"
                        />
                    </div>
                </div>
            )}

            {(snapshot?.wants_property_management
              || snapshot?.wants_investment_opportunities) && (
                <div className="mt-3 text-xs text-gray-500 space-y-0.5">
                    {snapshot.wants_property_management && (
                        <div className="font-semibold text-[#0e7490]">
                            Asked for help with property management. Follow up.
                        </div>
                    )}
                    {snapshot.wants_investment_opportunities && (
                        <div>Still wants investment opportunities, so alerts keep running.</div>
                    )}
                </div>
            )}

            {canEdit && (
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={!status || !dirty || saving}
                    className="mt-3 px-4 py-2 rounded-lg bg-[#55C5D0] text-white text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#3fb3bf] transition-colors"
                >
                    {saving ? 'Saving…' : 'Save search status'}
                </button>
            )}
        </div>
    );
};

export default ContactSearchStatusPanel;
